/**
 * Supported file extensions by media type. The media-type lists are xconfig's: the `decodable` set per media type of
 * MediaFormats.getMediaFormats() (xconfig src/definitions/media-formats), emitted as dist/media-formats.json and kept here
 * as the byte copy media-formats.generated.json (`npm run copy:media-formats`, pinned by
 * media-extensions.xconfig.drift.spec.ts). Until 2026-10-11 this file kept its own lists, which had drifted (audit R16:
 * APNG / ODG / PUB / ODT / ODS / ODP missing, CAF filed as video). Web-only policy lists (PDF / PS aliases, MS Office
 * family, thumbnails, the security deny-list) stay below.
 *
 * @module media-extensions
 */
import mediaFormats from './media-formats.generated.json';

type XconfigMediaType = keyof typeof mediaFormats.mediaTypes;

/** xconfig's decodable extensions for a media type ('FILE' is xconfig's "any file" archive input, not an extension). */
function xconfigDecodable(mediaType: XconfigMediaType): readonly string[] {
    return Object.freeze(mediaFormats.mediaTypes[mediaType].decodable.filter(ext => ext !== 'FILE'));
}

/** xconfig's encodable (output) extensions for a media type. */
function xconfigEncodable(mediaType: XconfigMediaType): readonly string[] {
    return Object.freeze(mediaFormats.mediaTypes[mediaType].encodable.filter(ext => ext !== 'FILE'));
}

/** All supported image extensions (xconfig IMAGE decodable) */
export const IMAGE_EXTENSIONS = xconfigDecodable('IMAGE');

/** All supported audio extensions (xconfig AUDIO decodable) */
export const AUDIO_EXTENSIONS = xconfigDecodable('AUDIO');

/** All supported video extensions (xconfig VIDEO decodable) */
export const VIDEO_EXTENSIONS = xconfigDecodable('VIDEO');

/** Every video container xconfig writes (xconfig VIDEO encodable): the `toType` values that mean "a video output". */
export const VIDEO_OUTPUT_EXTENSIONS = xconfigEncodable('VIDEO');

/** Archive extensions (xconfig ARCHIVE decodable) */
export const ARCHIVE_EXTENSIONS = xconfigDecodable('ARCHIVE');

/** Document extensions (xconfig DOCUMENT decodable) */
export const DOCUMENT_EXTENSIONS = xconfigDecodable('DOCUMENT');

/** Ebook extensions (xconfig EBOOK decodable) */
export const EBOOK_EXTENSIONS = xconfigDecodable('EBOOK');

/** Presentation extensions (xconfig PRESENTATION decodable) */
export const PRESENTATION_EXTENSIONS = xconfigDecodable('PRESENTATION');

/** Vector extensions (xconfig VECTOR decodable) */
export const VECTOR_EXTENSIONS = xconfigDecodable('VECTOR');

/** PDF extension */
export const PDF_EXTENSIONS = ['PDF'] as const;

/** PostScript extension */
export const PS_EXTENSIONS = ['PS'] as const;

/** MS Office extensions (for backward compatibility) */
export const MS_OFFICE_EXTENSIONS = [
    ...PRESENTATION_EXTENSIONS,
    'XLS', 'XLT', 'XLM', 'XLSX', 'XLSM', 'XLTX', 'XLTM',
    'DOC', 'DOT', 'WBK', 'DOCX', 'DOCM', 'DOTX', 'DOTM', 'DOCB'
] as const;

// ============================================================================
// AGGREGATED EXTENSION SETS
// ============================================================================

/** All supported extensions across all media types */
export const ALL_EXTENSIONS: readonly string[] = [
    ...IMAGE_EXTENSIONS,
    ...AUDIO_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...ARCHIVE_EXTENSIONS,
    ...DOCUMENT_EXTENSIONS,
    ...EBOOK_EXTENSIONS,
    ...PRESENTATION_EXTENSIONS,
    ...VECTOR_EXTENSIONS,
    ...MS_OFFICE_EXTENSIONS,
];

/** Set of all supported extensions for O(1) lookup */
export const ALL_EXTENSIONS_SET = new Set(ALL_EXTENSIONS);

/**
 * Image formats the file card has never asked a thumbnail for (they entered IMAGE_EXTENSIONS with xconfig's list on
 * 2026-10-11; office drawings, no browser or thumbnailer path verified). Web UI policy, not option data.
 */
const NO_THUMBNAIL = new Set(['ODG', 'PUB']);

/** Extensions that support thumbnail generation */
export const THUMBNAIL_SUPPORTED_EXTENSIONS: readonly string[] = [
    ...IMAGE_EXTENSIONS.filter(ext => !NO_THUMBNAIL.has(ext)),
    ...PDF_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
];

/** Set of thumbnail-supported extensions for O(1) lookup */
export const THUMBNAIL_SUPPORTED_EXTENSIONS_SET = new Set(THUMBNAIL_SUPPORTED_EXTENSIONS);

// ============================================================================
// DISALLOWED EXTENSIONS (Security)
// ============================================================================

/** Extensions that are not allowed for security reasons */
export const DISALLOWED_EXTENSIONS = new Set([
    // Executables and scripts
    'ADE', 'ADP', 'APK', 'APPX', 'APPXBUNDLE', 'BAT', 'CAB', 'CHM', 'CMD', 'COM', 'CPL',
    'DIAGCAB', 'DIAGCFG', 'DIAGPACK', 'DLL', 'DMG', 'EX', 'EX_', 'EXE', 'HTA', 'IMG',
    'INS', 'ISO', 'ISP', 'JAR', 'JNLP', 'JS', 'JSE', 'LIB', 'LNK', 'MDE', 'MSC', 'MSI',
    'MSIX', 'MSIXBUNDLE', 'MSP', 'MST', 'NSH', 'PIF', 'PS1', 'SCR', 'SCT', 'SHB', 'SYS',
    'VB', 'VBE', 'VBS', 'VHD', 'VXD', 'WSC', 'WSF', 'WSH', 'XLL',
    // Backup and config files
    'BAK', 'BCK', 'BKP', 'CFG', 'CONF', 'CONFIG', 'INI', 'LOG', 'OLD', 'SAV', 'SAVE',
    'TEMP', 'TMP', 'BIN', 'CGI', 'SHTM', 'SHTML', 'STM',
    // Certificate and key files
    'CER', 'CRT', 'DER', 'KEY', 'P12', 'P7B', 'P7C', 'PEM', 'PFX',
    // Other potentially dangerous files
    'DAT', 'EML', 'HTR', 'HTW', 'IDA', 'IDC', 'IDQ', 'NWS', 'POL', 'PRINTER', 'REG', 'WMZ'
]);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Extension strings (upper case, no dot). The lists are data from xconfig, so these are plain strings. */
export type ImageExtension = string;
export type AudioExtension = string;
export type VideoExtension = string;
export type ArchiveExtension = string;
export type DocumentExtension = string;
export type AllExtension = string;
